import { useCallback, useEffect, useState } from 'react';
import apiService from '../services/api';
import MerchantOnboarding from './MerchantOnboarding';

/**
 * Collection state and the ACH charge action for one merchant-month.
 *
 * Sits on the invoice page rather than the Billing list, so the list stays the
 * read-only report it is meant to be and money only ever moves from the page
 * showing the exact figure being charged.
 */

const money = (cents) => `$${(Number(cents || 0) / 100).toFixed(2)}`;

const TONE = {
  ok: { bg: '#e6f4ea', border: '#a5d6a7', text: '#1e7e34' },
  warn: { bg: '#fff8e1', border: '#ffe082', text: '#8a6100' },
  bad: { bg: '#fdecea', border: '#f5c6cb', text: '#b00020' },
  neutral: { bg: '#f8f9fa', border: '#e9ecef', text: '#495057' },
};

// What the merchant/invoice state means, and whether charging is possible.
const describe = (profile, invoice) => {
  if (invoice) {
    if (invoice.status === 'paid')
      return {
        tone: 'ok',
        // Nothing is outstanding, so there is nothing to explain. The heading
        // already reads "Paid" and a second line saying it again adds only the
        // date — so the date moves up into the heading and the banner stays a
        // single line.
        heading: `Paid${invoice.paid_at ? ` on ${new Date(invoice.paid_at).toLocaleDateString()}` : ''}`,
        text: null,
      };
    if (invoice.status === 'processing')
      return {
        tone: 'warn',
        text: `Submitted${invoice.charged_at ? ` on ${new Date(invoice.charged_at).toLocaleDateString()}` : ''}. ACH takes up to 4 business days to clear, and can still be returned after that.`,
      };
    if (invoice.status === 'disputed')
      return { tone: 'bad', text: invoice.failure_message || 'The bank returned this debit.' };
    if (invoice.status === 'failed')
      return { tone: 'bad', text: `Failed: ${invoice.failure_message || 'the debit did not go through.'}`, retry: true };
    if (invoice.status === 'canceled')
      return { tone: 'neutral', text: 'Canceled.' };
  }
  if (!profile?.exists)
    return { tone: 'neutral', text: 'No billing profile yet. Add a billing email, then send the ACH onboarding link.' };
  if (profile.mandate_status === 'active')
    return {
      tone: 'ok',
      // One sentence rather than a heading and a body that both open with
      // "Authorized". The address is the only thing here the reader does not
      // already know, so it takes the emphasis and the rest stays prose.
      text: profile.billing_email ? (
        <>
          Authorized by <strong>{profile.billing_email}</strong> for ACH debit.
        </>
      ) : (
        'Authorized for ACH debit.'
      ),
      // The sentence is the whole message; a heading above it would only
      // repeat its first word.
      standalone: Boolean(profile.billing_email),
      chargeable: true,
    };
  if (profile.mandate_status === 'pending')
    return {
      tone: 'warn',
      // The heading already reads "Awaiting bank verification", so the line
      // beneath it says why the wait is normal rather than repeating it.
      text: profile.mandate_status_detail || 'Microdeposits can take a few days to arrive.',
    };
  if (profile.mandate_status === 'revoked')
    return { tone: 'bad', text: profile.mandate_status_detail || 'Authorization was revoked. The merchant must authorize again before another debit.' };
  if (profile.mandate_status === 'blocked')
    return { tone: 'bad', text: profile.mandate_status_detail || 'Stripe blocked this bank account.' };
  return { tone: 'neutral', text: 'This merchant has not authorized ACH debits yet.' };
};

const BillingChargePanel = ({ merchantKey, merchantName, period, amountCents, lineItems }) => {
  const [state, setState] = useState(null);
  const [loading, setLoading] = useState(true);
  const [charging, setCharging] = useState(false);
  const [error, setError] = useState(null);
  // Set once a link has been generated here, so the panel keeps showing the
  // onboarding block rather than collapsing the moment the profile changes.
  const [linkGenerated, setLinkGenerated] = useState(false);

  const load = useCallback(async () => {
    if (!merchantKey || !period) return;
    try {
      const next = await apiService.getBillingStatus({ merchant_key: merchantKey, period });
      setState(next);
      setError(null);
    } catch (err) {
      console.error('Failed to load billing status:', err);
      setError('Could not load collection status.');
    } finally {
      setLoading(false);
    }
  }, [merchantKey, period]);

  useEffect(() => { load(); }, [load]);

  const charge = async () => {
    // ACH cannot be recalled, so the confirm states the amount and the risk.
    const ok = window.confirm(
      `Debit ${merchantName} ${money(amountCents)} for ${period}?\n\n` +
      'This starts an ACH withdrawal from their bank account. It takes up to 4 ' +
      'business days to clear, can fail or be returned after that, and cannot be ' +
      'recalled once submitted — only refunded, which takes 3 more business days.'
    );
    if (!ok) return;

    setCharging(true);
    setError(null);
    try {
      setState(await apiService.chargeBillingInvoice({
        merchant_key: merchantKey,
        period,
        amount_cents: amountCents,
        line_items: lineItems,
      }));
    } catch (err) {
      console.error('Charge failed:', err);
      setError(err?.response?.data?.error || 'The charge could not be submitted.');
      await load();
    } finally {
      setCharging(false);
    }
  };

  if (loading) return null;

  const info = describe(state?.profile, state?.invoice);
  const tone = TONE[info.tone] || TONE.neutral;
  const heading =
    info.heading ||
    state?.invoice?.status_label ||
    state?.profile?.mandate_status_label ||
    'Collection';
  const canCharge = Boolean(
    (info.chargeable || info.retry) && state?.profile?.can_be_charged && amountCents > 0
  );
  // Offer onboarding whenever the merchant cannot currently be debited, which
  // covers never-set-up, awaiting verification, revoked after a dispute, and
  // blocked accounts alike. `pending` is deliberately included: creating a link
  // moves the profile there, and a merchant who lost the link needs it again.
  // The first clause keeps a link we just generated on screen no matter what.
  const needsOnboarding = linkGenerated || (
    !state?.profile?.can_be_charged
    && !['paid', 'processing'].includes(state?.invoice?.status)
  );

  return (
    <div
      className="invoice-actions"
      style={{
        margin: '0 0 24px',
        padding: '16px 20px',
        borderRadius: '12px',
        background: tone.bg,
        border: `1px solid ${tone.border}`,
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        gap: '16px',
        flexWrap: 'wrap',
      }}
    >
      <div style={{ color: tone.text, fontSize: '14px', lineHeight: 1.5, maxWidth: '640px' }}>
        {!info.standalone && (
          <strong style={{ display: 'block', marginBottom: '2px' }}>{heading}</strong>
        )}
        {info.text}
        {error && <div style={{ marginTop: '6px', color: '#b00020' }}>{error}</div>}
      </div>

      {needsOnboarding && (
        <div style={{ flexBasis: '100%', borderTop: `1px solid ${tone.border}`, paddingTop: '14px' }}>
          <MerchantOnboarding
            merchantKey={merchantKey}
            merchantName={merchantName}
            profile={state?.profile}
            tone={tone}
            onProfileSaved={(profile) => {
              setLinkGenerated(true);
              if (profile) setState((prev) => ({ ...prev, profile }));
            }}
          />
        </div>
      )}

      {canCharge && (
        <button
          type="button"
          onClick={charge}
          disabled={charging}
          style={{
            padding: '10px 18px',
            border: 'none',
            borderRadius: '8px',
            background: charging ? '#9bb8e8' : '#0d6efd',
            color: 'white',
            fontWeight: 600,
            fontFamily: 'inherit',
            fontSize: '14px',
            cursor: charging ? 'default' : 'pointer',
            whiteSpace: 'nowrap',
          }}
        >
          {charging ? 'Submitting…' : `${info.retry ? 'Retry' : 'Debit'} ${money(amountCents)}`}
        </button>
      )}
    </div>
  );
};

export default BillingChargePanel;
